import { z } from 'zod';

// ---------------------------------------------------------------------------
// checklist_template(_item) / checklist_instance(_response) — fleet-qr-compliance
// PR2 (design.md §3). item_type is a closed Zod vocabulary with a single
// value in F1 (LOCX CLAUDE.md red line #1 — no new data_type from config);
// the column stays `text` at the DB layer to avoid an `ALTER TYPE` the day a
// second item type is added.
// ---------------------------------------------------------------------------

export const CHECKLIST_ITEM_TYPES = ['ok_fail_na'] as const;
export type ChecklistItemType = (typeof CHECKLIST_ITEM_TYPES)[number];

export const CHECKLIST_ANSWERS = ['ok', 'fail', 'na'] as const;
export type ChecklistAnswer = (typeof CHECKLIST_ANSWERS)[number];

export const CHECKLIST_CREATED_VIA = ['qr', 'admin'] as const;

// ---------------------------------------------------------------------------
// Template — admin CRUD, versioned (edit = new row, old marked inactive)
// ---------------------------------------------------------------------------

export const ChecklistTemplateItemInputSchema = z.object({
  label: z.string().min(1),
  itemType: z.enum(CHECKLIST_ITEM_TYPES).default('ok_fail_na'),
  required: z.boolean().default(true),
  sortOrder: z.number().int().nonnegative(),
  /** Flat grouping label (e.g. 'MOTOR Y FLUIDOS') — omit for an ungrouped item. Migration 0114. */
  category: z.string().min(1).optional(),
});
export type ChecklistTemplateItemInput = z.infer<typeof ChecklistTemplateItemInputSchema>;

/**
 * Creates a NEW template version. If `name` matches an existing active
 * template, that one is deactivated and this becomes its successor
 * (supersedesId) — items are never edited in place (design.md §3).
 */
export const ChecklistTemplateCreateSchema = z.object({
  name: z.string().min(1),
  frequencyDays: z.number().int().positive(),
  items: z.array(ChecklistTemplateItemInputSchema).min(1, 'At least one item is required'),
});
export type ChecklistTemplateCreate = z.infer<typeof ChecklistTemplateCreateSchema>;

export const ChecklistTemplateItemResponseSchema = z.object({
  id: z.string().uuid(),
  templateId: z.string().uuid(),
  label: z.string(),
  itemType: z.enum(CHECKLIST_ITEM_TYPES),
  required: z.boolean(),
  sortOrder: z.number(),
  category: z.string().nullable(),
});
export type ChecklistTemplateItemResponse = z.infer<typeof ChecklistTemplateItemResponseSchema>;

export const ChecklistTemplateResponseSchema = z.object({
  id: z.string().uuid(),
  companyId: z.string().uuid(),
  name: z.string(),
  frequencyDays: z.number(),
  active: z.boolean(),
  supersedesId: z.string().uuid().nullable(),
  createdAt: z.string().datetime().or(z.date()),
  items: z.array(ChecklistTemplateItemResponseSchema),
});
export type ChecklistTemplateResponse = z.infer<typeof ChecklistTemplateResponseSchema>;

// ---------------------------------------------------------------------------
// Submission — idempotent by (assetId, periodKey); templateId identifies the
// exact version answered.
// ---------------------------------------------------------------------------

export const ChecklistResponseAnswerSchema = z.object({
  itemId: z.string().uuid(),
  answer: z.enum(CHECKLIST_ANSWERS),
  observation: z.string().optional(),
  /** Only meaningful when answer='fail' — validated by the use-case, not here. */
  photoFileId: z.string().uuid().optional(),
});
export type ChecklistResponseAnswer = z.infer<typeof ChecklistResponseAnswerSchema>;

export const ChecklistSubmitSchema = z.object({
  templateId: z.string().uuid(),
  /** Minimal identification of the responsible person — NO auth, free text (design.md §4.4). */
  respondedByName: z.string().min(1),
  notes: z.string().optional(),
  responses: z.array(ChecklistResponseAnswerSchema).min(1, 'At least one response is required'),
});
export type ChecklistSubmit = z.infer<typeof ChecklistSubmitSchema>;

export const ChecklistResponseItemResponseSchema = z.object({
  id: z.string().uuid(),
  itemId: z.string().uuid(),
  answer: z.enum(CHECKLIST_ANSWERS),
  observation: z.string().nullable(),
  photoFileId: z.string().uuid().nullable(),
});
export type ChecklistResponseItemResponse = z.infer<typeof ChecklistResponseItemResponseSchema>;

export const ChecklistInstanceResponseSchema = z.object({
  id: z.string().uuid(),
  companyId: z.string().uuid(),
  assetId: z.string().uuid(),
  templateId: z.string().uuid(),
  periodKey: z.string(),
  respondedByName: z.string(),
  createdVia: z.enum(CHECKLIST_CREATED_VIA),
  notes: z.string().nullable(),
  submittedAt: z.string().datetime().or(z.date()),
  updatedAt: z.string().datetime().or(z.date()),
  responses: z.array(ChecklistResponseItemResponseSchema),
});
export type ChecklistInstanceResponse = z.infer<typeof ChecklistInstanceResponseSchema>;
